/**
 * Admin Business Quick Booking — Phase 2G (2026-04-26)
 * /admin/business-quick
 *
 * Oversight surface for all business-quick users:
 *   list / search · create · reset-pin · suspend · activate
 *
 * Backend: /api/admin/business-quick/* (admin role required)
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Loader2, Building2, Plus, RotateCcw, Pause, Play, Search,
} from "lucide-react";

const PARISHES = [
  "Kingston", "St. Andrew", "Kingston and St. Andrew",
  "St. Catherine", "Clarendon", "Manchester", "St. Elizabeth",
  "Westmoreland", "Hanover", "St. James", "Trelawny",
  "St. Ann", "St. Mary", "Portland", "St. Thomas",
];

export default function AdminBusinessQuick() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [working, setWorking] = useState(null);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/business-quick/users");
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load businesses");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const resetPin = async (bid) => {
    if (!window.confirm("Reset PIN and email it to the business?")) return;
    setWorking(bid);
    try {
      await api.post(`/admin/business-quick/users/${bid}/reset-pin`);
      toast.success("PIN reset; new PIN emailed to business");
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setWorking(null); reload(); }
  };

  const suspend = async (bid) => {
    const reason = window.prompt("Reason for suspension (≥10 chars)?");
    if (!reason || reason.length < 10) return toast.error("Reason required (≥10 chars)");
    setWorking(bid);
    try {
      await api.post(`/admin/business-quick/users/${bid}/suspend`, { reason });
      toast.success("Business suspended");
    } catch (e) { toast.error(e?.response?.data?.detail || "Suspend failed"); }
    finally { setWorking(null); reload(); }
  };

  const activate = async (bid) => {
    setWorking(bid);
    try {
      await api.post(`/admin/business-quick/users/${bid}/activate`);
      toast.success("Business activated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Activate failed"); }
    finally { setWorking(null); reload(); }
  };

  const filtered = rows.filter(r => {
    if (!q) return true;
    const s = `${r.business_name} ${r.contact_name} ${r.email} ${r.phone} ${r.parish}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-business-quick">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-600" />
            <h1 className="text-lg font-bold">Business Quick — Oversight</h1>
          </div>
          <Button data-testid="abq-new" size="sm" onClick={() => setShowNew(true)}>
            <Plus className="w-4 h-4 mr-1" />Onboard business
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <Input data-testid="abq-search" placeholder="Search by name, email, phone, parish…"
            value={q} onChange={e => setQ(e.target.value)} className="max-w-sm" />
          <button onClick={reload} className="text-xs text-slate-500 underline ml-auto">Refresh</button>
        </div>

        {busy ? (
          <div className="text-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin inline mr-1" />Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-slate-500" data-testid="abq-empty">
            <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No businesses match — onboard the first one.
          </div>
        ) : (
          <div className="overflow-x-auto bg-white border border-slate-200 rounded-2xl">
            <table className="w-full text-sm" data-testid="abq-table">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left p-2">Business</th>
                  <th className="text-left p-2">Contact</th>
                  <th className="text-left p-2">Parish</th>
                  <th className="text-left p-2">Source</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`abq-row-${r.id}`}>
                    <td className="p-2">
                      <div className="font-medium">{r.business_name}</div>
                      <div className="text-xs text-slate-500">{r.email}</div>
                    </td>
                    <td className="p-2">
                      <div>{r.contact_name}</div>
                      <div className="text-xs text-slate-500">{r.phone}</div>
                    </td>
                    <td className="p-2">{r.parish}</td>
                    <td className="p-2 text-xs text-slate-500">{r.creation_source}</td>
                    <td className="p-2">
                      {r.is_active ? (
                        <span className="text-xs bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">ACTIVE</span>
                      ) : (
                        <span className="text-xs bg-rose-100 text-rose-800 rounded-full px-2 py-0.5">SUSPENDED</span>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" disabled={working === r.id}
                          data-testid={`abq-reset-${r.id}`} onClick={() => resetPin(r.id)}>
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                        {r.is_active ? (
                          <Button size="sm" variant="outline" disabled={working === r.id}
                            data-testid={`abq-suspend-${r.id}`} onClick={() => suspend(r.id)}>
                            <Pause className="w-3 h-3" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" disabled={working === r.id}
                            data-testid={`abq-activate-${r.id}`} onClick={() => activate(r.id)}>
                            <Play className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && (
        <NewBusinessDialog
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); reload(); }}
          endpoint="/admin/business-quick/users"
        />
      )}
    </div>
  );
}

export function NewBusinessDialog({ onClose, onSaved, endpoint }) {
  const [f, setF] = useState({
    business_name: "", contact_name: "", phone: "", email: "",
    address: "", parish: "Kingston", community: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    for (const k of ["business_name", "contact_name", "phone", "email", "address", "parish"]) {
      if (!f[k]) return toast.error(`${k} required`);
    }
    setBusy(true);
    try {
      const r = await api.post(endpoint, f);
      const who = r.data?.email || r.data?.business_name || "business";
      toast.success(`Onboarded ${who} — PIN emailed`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Onboarding failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="abq-new-dialog">
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2"><Plus className="w-4 h-4" />Onboard business</h3>
          <button onClick={onClose} className="text-slate-400 text-sm">close</button>
        </div>
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          A 6-digit PIN is auto-generated and emailed via SendGrid. The business logs in at <strong>liantiaoja.com/business-quick/login</strong>.
        </div>
        <Input data-testid="abq-new-name" placeholder="Business name" value={f.business_name} onChange={set("business_name")} />
        <Input data-testid="abq-new-contact" placeholder="Contact person" value={f.contact_name} onChange={set("contact_name")} />
        <Input data-testid="abq-new-phone" placeholder="Phone (e.g., +18761234567)" value={f.phone} onChange={set("phone")} />
        <Input data-testid="abq-new-email" placeholder="Email" type="email" value={f.email} onChange={set("email")} />
        <Input data-testid="abq-new-address" placeholder="Pickup address" value={f.address} onChange={set("address")} />
        <select data-testid="abq-new-parish" className="w-full border rounded p-2 text-sm" value={f.parish} onChange={set("parish")}>
          {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <Input data-testid="abq-new-community" placeholder="Community (optional)" value={f.community} onChange={set("community")} />
        <Input data-testid="abq-new-notes" placeholder="Notes (optional)" value={f.notes} onChange={set("notes")} />
        <Button data-testid="abq-new-submit" disabled={busy} onClick={submit} className="w-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Onboard & email PIN
        </Button>
      </div>
    </div>
  );
}
